import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FilePlus, Loader2, Lock, MailPlus, SlidersHorizontal, Upload } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { formatDate } from '../../../config/appConfig';
import { useTheme, type ThemeColors } from '../../../contexts/ThemeContext';
import {
  importTemplatesFolder,
  updateTemplateFolderApi,
  copyTemplateFolderApi,
  deleteTemplateFolderApi,
  deleteTemplateApi,
  downloadTemplateDocumentApi,
  deleteTemplateDocumentApi,
  uploadTemplateDocumentApi,
  type EmailTemplate,
  type EmailTemplateDocument,
  type EmailTemplateFolder,
  type EmailTemplateLibrarySection,
  type SharedEmailTemplateFolder,
} from '../useData';
import CreateTemplateFolderModal from './CreateTemplateFolderModal';
import TemplateFolderOptionsModal from './TemplateFolderOptionsModal';
import ImportTemplatesLibraryModal from './ImportTemplatesLibraryModal';
import TemplateFolderImportProgressModal, {
  type FolderImportProgressState,
} from './TemplateFolderImportProgressModal';
import TemplateFolderDeleteProgressModal, {
  type FolderDeleteProgressState,
} from './TemplateFolderDeleteProgressModal';
import TemplateFolderCopyProgressModal, {
  type FolderCopyProgressState,
} from './TemplateFolderCopyProgressModal';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../../../components/ui/Modal';
import EntityCustomFieldsSection from '../../custom-fields/EntityCustomFieldsSection';
import { CUSTOM_FIELD_ENTITY } from '../../custom-fields/entityTypes';
import { FileIcon } from '../../../components/attachments/FileIcon';
import {
  pickImportFolder,
  readImportFolderFiles,
  readDataTransferImportFiles,
  folderNameFromWebkitFiles,
  isImportFolderAvailable,
  isImportFolderLanDev,
  type ImportDirectoryHandle,
} from '../utils/pickImportFolder';
import {
  folderImportErrorMessage,
  validateFolderImportFiles,
} from '../utils/folderImportLimits';
import {
  captureComposeReturnTarget,
  navigateToCompose,
  withLibraryComposeReturn,
  type ComposeListRestoreState,
  type ComposeReturnTarget,
  type TemplateLibrarySelection,
} from '../utils/composeNavigation';
import { useEmailUiPreferences } from '../hooks/useEmailUiPreferences';
import {
  LIBRARY_FOLDER_SPLIT_MAX,
  LIBRARY_FOLDER_SPLIT_MIN,
} from '../utils/emailUiPreferencesStorage';

export type { TemplateLibrarySelection };

type LibraryRoot = 'company' | 'personal' | 'shared';
type FolderTreeNode = EmailTemplateFolder & { children: FolderTreeNode[] };

/** Virtual id for the Shared library section (not a DB folder). */
const ROOT_KEYS = {
  shared: 'root-shared',
} as const;

function buildFolderTree(folders: EmailTemplateFolder[]): FolderTreeNode[] {
  const byId = new Map<number, FolderTreeNode>();
  for (const f of folders) {
    byId.set(f.id, { ...f, children: [] });
  }
  const roots: FolderTreeNode[] = [];
  for (const node of Array.from(byId.values())) {
    if (node.parentId != null && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortNodes = (nodes: FolderTreeNode[]) => {
    nodes.sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}

function collectSharedSubtreeIds(
  rootFolderId: number,
  includeSubfolders: boolean,
  childFolders: Array<{ id: number; parentId: number | null }>,
): number[] {
  const ids = new Set<number>([rootFolderId]);
  if (!includeSubfolders) return [rootFolderId];
  let frontier = [rootFolderId];
  while (frontier.length > 0) {
    const next: number[] = [];
    childFolders.forEach((f) => {
      if (f.parentId != null && frontier.includes(f.parentId) && !ids.has(f.id)) {
        ids.add(f.id);
        next.push(f.id);
      }
    });
    frontier = next;
  }
  return Array.from(ids);
}

function collectAncestorFolderIds(folderId: number, folders: EmailTemplateFolder[]): number[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const ids: number[] = [];
  let current = byId.get(folderId);
  while (current?.parentId != null) {
    ids.push(current.parentId);
    current = byId.get(current.parentId);
  }
  return ids;
}

function toFolderTreeNode(
  id: number,
  name: string,
  parentId: number | null,
  children: FolderTreeNode[] = [],
): FolderTreeNode {
  return { id, name, parentId, displayOrder: 0, children };
}

function collectFolderIds(node: FolderTreeNode): number[] {
  return [node.id, ...node.children.flatMap(collectFolderIds)];
}

function findFolderNode(nodes: FolderTreeNode[], id: number): FolderTreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findFolderNode(n.children, id);
    if (found) return found;
  }
  return null;
}

function countTemplatesInFolder(node: FolderTreeNode, templates: EmailTemplate[]): number {
  const ids = new Set(collectFolderIds(node));
  return templates.filter((t) => t.folderId != null && ids.has(t.folderId)).length;
}

function countDocumentsInFolder(node: FolderTreeNode, documents: EmailTemplateDocument[]): number {
  const ids = new Set(collectFolderIds(node));
  return documents.filter((d) => ids.has(d.folderId)).length;
}

function countSubtreeSubfolders(node: FolderTreeNode): number {
  return collectFolderIds(node).length - 1;
}

function countSharedFolderTemplates(
  folderId: number,
  childFolders: Array<{ id: number; parentId: number | null }>,
  templates: EmailTemplate[],
): number {
  const ids = new Set(collectSharedSubtreeIds(folderId, true, childFolders));
  return templates.filter((t) => t.folderId != null && ids.has(t.folderId)).length;
}

const LIBRARY_TOOLBAR_ICON = { size: 20, strokeWidth: 1.75 } as const;

const ChevronDown = (
  <svg className="w-4 h-4 flex-shrink-0 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);
const ChevronRight = (
  <svg className="w-4 h-4 flex-shrink-0 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);
const FolderIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);
const GearIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

function formatFolderLabel(name: string): string {
  const trimmed = name.trim();
  return trimmed || 'Untitled folder';
}

function isFolderPathLocked(
  folderId: number | null | undefined,
  folders: EmailTemplateFolder[],
): boolean {
  if (folderId == null) return false;
  const byId = new Map(folders.map((f) => [f.id, f]));
  let current: number | null = folderId;
  while (current != null) {
    const folder = byId.get(current);
    if (!folder) break;
    if (folder.isLocked) return true;
    current = folder.parentId;
  }
  return false;
}

function hasLockedAncestor(
  folderId: number,
  folders: EmailTemplateFolder[],
): boolean {
  const byId = new Map(folders.map((f) => [f.id, f]));
  let current = byId.get(folderId)?.parentId ?? null;
  while (current != null) {
    const folder = byId.get(current);
    if (!folder) break;
    if (folder.isLocked) return true;
    current = folder.parentId;
  }
  return false;
}

function foldersForLibraryItem(
  scope: 'PERSONAL' | 'COMPANY' | undefined,
  companyFolders: EmailTemplateFolder[],
  personalFolders: EmailTemplateFolder[],
): EmailTemplateFolder[] {
  return scope === 'COMPANY' ? companyFolders : personalFolders;
}

function canDeleteTemplate(
  t: EmailTemplate,
  companyCanEdit: boolean,
  companyFolders: EmailTemplateFolder[],
  personalFolders: EmailTemplateFolder[],
): boolean {
  const folders = foldersForLibraryItem(t.scope, companyFolders, personalFolders);
  if (isFolderPathLocked(t.folderId, folders)) return false;
  if (t.isOwner === false || t.ownerName) return false;
  if (t.scope === 'COMPANY') return companyCanEdit;
  return true;
}

function canDeleteDocument(
  doc: EmailTemplateDocument,
  companyCanEdit: boolean,
  companyFolders: EmailTemplateFolder[],
  personalFolders: EmailTemplateFolder[],
): boolean {
  const folders = foldersForLibraryItem(doc.scope, companyFolders, personalFolders);
  if (isFolderPathLocked(doc.folderId, folders)) return false;
  if (doc.scope === 'COMPANY') return companyCanEdit;
  return true;
}

function isLibraryItemPathLocked(
  folderId: number | null | undefined,
  scope: 'PERSONAL' | 'COMPANY' | undefined,
  companyFolders: EmailTemplateFolder[],
  personalFolders: EmailTemplateFolder[],
): boolean {
  return isFolderPathLocked(
    folderId,
    foldersForLibraryItem(scope, companyFolders, personalFolders),
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function libraryItemIconFileName(item: { kind: 'template'; template: EmailTemplate } | { kind: 'document'; document: EmailTemplateDocument }): string {
  if (item.kind === 'document') return item.document.name;
  const name = item.template.name?.trim();
  if (name && /\.[a-z0-9]{2,5}$/i.test(name)) return name;
  return 'template.eml';
}

function filterDocumentsByFolder(
  pool: EmailTemplateDocument[],
  folderId: number | undefined,
): EmailTemplateDocument[] {
  if (folderId === undefined) return pool;
  return pool.filter((d) => d.folderId === folderId);
}

/** Flex row: indented caption (chevron + label) and fixed gear column. */
const TREE_ROW_FLEX = 'flex w-full min-w-0 items-center';
const GEAR_SLOT = 'relative z-10 w-7 flex-shrink-0 flex items-center justify-center';

function FolderTreeRowShell({
  depth,
  hasChildren,
  expanded,
  onToggleExpand,
  selected,
  onSelect,
  label,
  title,
  leadingIcon,
  showLocked,
  onOpenFolderOptions,
  optionsAriaLabel,
  rowBase,
  rowPaddingClass,
  rowSelected,
  rowDefault,
  colors,
}: {
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
  onToggleExpand?: () => void;
  selected: boolean;
  onSelect: () => void;
  label: string;
  title?: string;
  leadingIcon?: React.ReactNode;
  showLocked?: boolean;
  onOpenFolderOptions?: () => void;
  optionsAriaLabel?: string;
  rowBase: string;
  rowPaddingClass: string;
  rowSelected: string;
  rowDefault: string;
  colors: ThemeColors;
}) {
  const indentPl = 4 + depth * 12;

  return (
    <div className={TREE_ROW_FLEX}>
      <div
        className={cn(
          rowBase,
          rowPaddingClass,
          'flex flex-1 min-w-0 items-center gap-0.5 overflow-hidden',
          selected ? rowSelected : rowDefault,
        )}
        style={{ paddingLeft: indentPl }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand?.();
            }}
            className={cn('w-5 flex-shrink-0 flex items-center justify-center p-0.5 rounded', colors.interactiveHover)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? ChevronDown : ChevronRight}
          </button>
        ) : (
          <span className="w-5 flex-shrink-0 inline-block" aria-hidden />
        )}
        <button
          type="button"
          onClick={onSelect}
          className="flex flex-1 min-w-0 items-center gap-2 overflow-hidden text-left text-inherit bg-transparent border-0 p-0"
          title={title ?? label}
        >
          {leadingIcon ?? FolderIcon}
          <span className="truncate flex-1 min-w-0">{label}</span>
          {showLocked ? (
            <span title="Locked" className="inline-flex flex-shrink-0">
              <Lock className="w-3.5 h-3.5 opacity-70" aria-hidden />
            </span>
          ) : null}
        </button>
      </div>
      <div className={GEAR_SLOT}>
        {onOpenFolderOptions ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenFolderOptions();
            }}
            className={cn('p-1 rounded', colors.textMuted, colors.interactiveHover)}
            title="Folder options"
            aria-label={optionsAriaLabel ?? `Folder options for ${label}`}
          >
            {GearIcon}
          </button>
        ) : (
          <span className="w-7 inline-block" aria-hidden />
        )}
      </div>
    </div>
  );
}

function countDirectTemplatesInFolder(folderId: number, templates: EmailTemplate[]): number {
  return templates.filter((t) => t.folderId === folderId).length;
}

function TemplateFolderTree({
  node,
  depth,
  isSelected,
  isExpanded,
  onToggleExpand,
  onSelectFolder,
  onOpenFolderOptions,
  isPathLocked,
  rowBase,
  rowLevel1,
  rowSelected,
  rowDefault,
  colors,
}: {
  node: FolderTreeNode;
  depth: number;
  isSelected: (id: number) => boolean;
  isExpanded: (id: number) => boolean;
  onToggleExpand: (id: number) => void;
  onSelectFolder: (id: number) => void;
  onOpenFolderOptions?: (folder: FolderTreeNode) => void;
  isPathLocked?: (folderId: number) => boolean;
  rowBase: string;
  rowLevel1: string;
  rowSelected: string;
  rowDefault: string;
  colors: ThemeColors;
}) {
  const hasChildren = node.children.length > 0;
  const expanded = isExpanded(node.id);
  const selected = isSelected(node.id);

  return (
    <div className="mt-0" role="treeitem" aria-expanded={hasChildren ? expanded : undefined} aria-selected={selected}>
      <FolderTreeRowShell
        depth={depth}
        hasChildren={hasChildren}
        expanded={expanded}
        onToggleExpand={() => onToggleExpand(node.id)}
        selected={selected}
        onSelect={() => onSelectFolder(node.id)}
        label={formatFolderLabel(node.name)}
        showLocked={isPathLocked?.(node.id)}
        onOpenFolderOptions={onOpenFolderOptions ? () => onOpenFolderOptions(node) : undefined}
        rowBase={rowBase}
        rowPaddingClass={rowLevel1}
        rowSelected={rowSelected}
        rowDefault={rowDefault}
        colors={colors}
      />
      {hasChildren && expanded && (
        <div role="group">
          {node.children.map((child) => (
            <TemplateFolderTree
              key={child.id}
              node={child}
              depth={depth + 1}
              isSelected={isSelected}
              isExpanded={isExpanded}
              onToggleExpand={onToggleExpand}
              onSelectFolder={onSelectFolder}
              onOpenFolderOptions={onOpenFolderOptions}
              isPathLocked={isPathLocked}
              rowBase={rowBase}
              rowLevel1={rowLevel1}
              rowSelected={rowSelected}
              rowDefault={rowDefault}
              colors={colors}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SharedTemplateFolderTree({
  sf,
  depth,
  childFolders,
  isSelected,
  isExpanded,
  onToggleExpand,
  onSelect,
  onOpenFolderOptions,
  rowBase,
  rowLevel1,
  rowSelected,
  rowDefault,
  colors,
}: {
  sf: SharedEmailTemplateFolder;
  depth: number;
  childFolders: Array<{ id: number; name: string; parentId: number | null }>;
  isSelected: (folderId: number) => boolean;
  isExpanded: (folderId: number) => boolean;
  onToggleExpand: (folderId: number) => void;
  onSelect: (folderId: number) => void;
  onOpenFolderOptions?: (sf: SharedEmailTemplateFolder) => void;
  rowBase: string;
  rowLevel1: string;
  rowSelected: string;
  rowDefault: string;
  colors: ThemeColors;
}) {
  const children = childFolders.filter((f) => f.parentId === sf.folderId);
  const hasChildren = children.length > 0;
  const expanded = isExpanded(sf.folderId);
  const selected = isSelected(sf.folderId);

  return (
    <div className="mt-0" role="treeitem" aria-expanded={hasChildren ? expanded : undefined} aria-selected={selected}>
      <FolderTreeRowShell
        depth={depth}
        hasChildren={hasChildren}
        expanded={expanded}
        onToggleExpand={() => onToggleExpand(sf.folderId)}
        selected={selected}
        onSelect={() => onSelect(sf.folderId)}
        label={formatFolderLabel(sf.name)}
        title={`${formatFolderLabel(sf.name)} (${sf.ownerName})`}
        onOpenFolderOptions={onOpenFolderOptions ? () => onOpenFolderOptions(sf) : undefined}
        rowBase={rowBase}
        rowPaddingClass={rowLevel1}
        rowSelected={rowSelected}
        rowDefault={rowDefault}
        colors={colors}
      />
      {hasChildren && expanded && (
        <div role="group">
          {children.map((child) => {
            const pseudoShare: SharedEmailTemplateFolder = {
              ...sf,
              folderId: child.id,
              name: child.name,
              parentId: child.parentId,
            };
            return (
              <SharedTemplateFolderTree
                key={child.id}
                sf={pseudoShare}
                depth={depth + 1}
                childFolders={childFolders}
                isSelected={isSelected}
                isExpanded={isExpanded}
                onToggleExpand={onToggleExpand}
                onSelect={onSelect}
                onOpenFolderOptions={onOpenFolderOptions}
                rowBase={rowBase}
                rowLevel1={rowLevel1}
                rowSelected={rowSelected}
                rowDefault={rowDefault}
                colors={colors}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function TemplateLibraryRootRow({
  label,
  expanded,
  onToggleExpand,
  selected,
  onSelect,
  onOpenFolderOptions,
  rowBase,
  rowLevel0,
  rowSelected,
  rowDefault,
  colors,
  children,
}: {
  label: string;
  expanded: boolean;
  onToggleExpand: () => void;
  selected: boolean;
  onSelect: () => void;
  onOpenFolderOptions?: () => void;
  rowBase: string;
  rowLevel0: string;
  rowSelected: string;
  rowDefault: string;
  colors: ThemeColors;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('mt-1.5 pt-1.5 border-t first:mt-0 first:pt-0 first:border-t-0', colors.border)}>
      <FolderTreeRowShell
        depth={0}
        hasChildren
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        selected={selected}
        onSelect={onSelect}
        label={label}
        onOpenFolderOptions={onOpenFolderOptions}
        optionsAriaLabel={`Folder options for ${label}`}
        rowBase={rowBase}
        rowPaddingClass={rowLevel0}
        rowSelected={rowSelected}
        rowDefault={rowDefault}
        colors={colors}
      />
      {expanded && (
        <div role="group" aria-label={`${label} folders`}>
          {children}
        </div>
      )}
    </div>
  );
}

interface TemplatesLibraryPanelProps {
  company: EmailTemplateLibrarySection & { canEdit: boolean };
  personal: EmailTemplateLibrarySection;
  shared: { folders: SharedEmailTemplateFolder[]; templates: EmailTemplate[] };
  isLoading?: boolean;
  /** Folder import — only enabled on the standalone /library page. */
  showImport?: boolean;
  /** Compose picker: folder tree + items only, no edit toolbar. */
  pickerMode?: 'template' | 'document';
  onPickTemplate?: (template: EmailTemplate) => void;
  selectedDocumentIds?: ReadonlySet<number>;
  onToggleDocument?: (documentId: number) => void;
  onImported: () => void | Promise<void>;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  onShareTemplateFolder?: (folder: { id: number; name: string }) => void;
  /** Where to return when closing compose opened from this panel. */
  composeReturnTo?: ComposeReturnTarget;
}

const TemplatesLibraryPanel: React.FC<TemplatesLibraryPanelProps> = ({
  company,
  personal,
  shared,
  isLoading,
  showImport = false,
  pickerMode,
  onPickTemplate,
  selectedDocumentIds,
  onToggleDocument,
  onImported,
  onError,
  onSuccess,
  onShareTemplateFolder,
  composeReturnTo,
}) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { prefs: emailUiPrefs, updatePrefs: updateEmailUiPrefs } = useEmailUiPreferences();
  const returnTo = composeReturnTo ?? captureComposeReturnTarget(location);

  const rowBase = 'flex items-center gap-2 min-w-0 text-left text-sm rounded-md transition-colors border-l-2 border-transparent';
  const rowLevel0 = 'py-1 pl-1 pr-2';
  const rowLevel1 = 'py-1 pl-5 pr-2';
  const rowSelected = cn(colors.primary, colors.primaryText, 'font-medium', 'border-l-indigo-500');
  const rowDefault = cn(colors.text, colors.interactiveHover);

  const rowProps = { rowBase, rowLevel0, rowLevel1, rowSelected, rowDefault, colors };

  const folderInputRef = useRef<HTMLInputElement>(null);
  const newFileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<FolderImportProgressState | null>(null);
  const [deleteProgress, setDeleteProgress] = useState<FolderDeleteProgressState | null>(null);
  const [copyProgress, setCopyProgress] = useState<FolderCopyProgressState | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [folderUploadConfirm, setFolderUploadConfirm] = useState<{
    folderName: string;
    mode: 'directory' | 'legacy';
  } | null>(null);
  const pendingDirectoryHandleRef = useRef<ImportDirectoryHandle | null>(null);
  const pendingLegacyFilesRef = useRef<File[] | null>(null);
  const importDragDepthRef = useRef(0);
  const [importDragOver, setImportDragOver] = useState(false);
  const libraryRestoreAppliedRef = useRef<string | null>(null);
  const [selection, setSelection] = useState<TemplateLibrarySelection>(() => {
    const state = location.state as ComposeListRestoreState | null;
    return state?.restoreLibrarySelection ?? { root: 'company' };
  });
  const [highlightTemplateId, setHighlightTemplateId] = useState<number | null>(() => {
    const state = location.state as ComposeListRestoreState | null;
    return state?.restoreLibraryTemplateId ?? null;
  });
  const [expandedRoots, setExpandedRoots] = useState<Set<string>>(
    () => new Set([ROOT_KEYS.shared]),
  );
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<number>>(() => new Set());
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [createFolderScope, setCreateFolderScope] = useState<'PERSONAL' | 'COMPANY'>('COMPANY');
  const [createFolderParentId, setCreateFolderParentId] = useState<number | undefined>(undefined);
  const [createFolderParentName, setCreateFolderParentName] = useState<string | undefined>(undefined);
  const [folderOptions, setFolderOptions] = useState<{
    folder: FolderTreeNode;
    scope: 'PERSONAL' | 'COMPANY' | 'SHARED';
    permission?: 'read' | 'write';
    ownerName?: string;
  } | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<EmailTemplateDocument | null>(null);
  const [deletingDocument, setDeletingDocument] = useState(false);
  const [customFieldsTarget, setCustomFieldsTarget] = useState<
    | { kind: 'template'; id: number; label: string }
    | { kind: 'document'; id: number; label: string }
    | null
  >(null);
  const [folderSplitPercent, setFolderSplitPercent] = useState(emailUiPrefs.libraryFolderSplitPercent);
  const [isResizingFolderSplit, setIsResizingFolderSplit] = useState(false);
  const folderSplitPercentRef = useRef(folderSplitPercent);
  folderSplitPercentRef.current = folderSplitPercent;

  useEffect(() => {
    setFolderSplitPercent(emailUiPrefs.libraryFolderSplitPercent);
  }, [emailUiPrefs.libraryFolderSplitPercent]);

  const handleFolderSplitMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingFolderSplit(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingFolderSplit) return;

      const container = document.querySelector('.library-split-container');
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const next = ((e.clientX - rect.left) / rect.width) * 100;
      const constrained = Math.max(
        LIBRARY_FOLDER_SPLIT_MIN,
        Math.min(LIBRARY_FOLDER_SPLIT_MAX, next),
      );
      setFolderSplitPercent(constrained);
    };

    const handleMouseUp = () => {
      if (isResizingFolderSplit) {
        updateEmailUiPrefs({ libraryFolderSplitPercent: Math.round(folderSplitPercentRef.current) });
        setIsResizingFolderSplit(false);
      }
    };

    if (isResizingFolderSplit) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingFolderSplit, updateEmailUiPrefs]);

  const composeReturnWithLibrary = (templateId?: number) =>
    withLibraryComposeReturn(returnTo, { selection, templateId });

  const companyTree = useMemo(() => buildFolderTree(company.folders), [company.folders]);
  const personalFoldersMerged = useMemo(() => {
    const byId = new Map(personal.folders.map((f) => [f.id, f]));
    for (const t of personal.templates) {
      if (t.folderId != null && t.folder && !byId.has(t.folderId)) {
        byId.set(t.folderId, {
          id: t.folder.id,
          name: t.folder.name,
          parentId: t.folder.parentId,
          displayOrder: 999,
        });
      }
    }
    return Array.from(byId.values());
  }, [personal.folders, personal.templates]);
  const personalTree = useMemo(() => buildFolderTree(personalFoldersMerged), [personalFoldersMerged]);

  useEffect(() => {
    const systemIds = [...company.folders, ...personal.folders]
      .filter((f) => f.isSystemRoot)
      .map((f) => f.id);
    if (systemIds.length > 0) {
      setExpandedFolderIds((prev) => new Set([...Array.from(prev), ...systemIds]));
    }
  }, [company.folders, personal.folders]);

  const personalTreeExpandInitialized = useRef(false);
  useEffect(() => {
    if (personalTreeExpandInitialized.current || personalTree.length === 0) return;
    personalTreeExpandInitialized.current = true;
    const ids = new Set<number>();
    const walk = (nodes: FolderTreeNode[]) => {
      for (const n of nodes) {
        if (n.children.length > 0) {
          ids.add(n.id);
          walk(n.children);
        }
      }
    };
    walk(personalTree);
    if (ids.size > 0) {
      setExpandedFolderIds((prev) => new Set([...Array.from(prev), ...Array.from(ids)]));
    }
  }, [personalTree]);

  const expandFolderBranch = useCallback((folderId: number, folders: EmailTemplateFolder[]) => {
    const branchIds = new Set([folderId, ...collectAncestorFolderIds(folderId, folders)]);
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      branchIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const sharedChildFolders = useMemo(() => {
    const map = new Map<number, { id: number; name: string; parentId: number | null }>();
    for (const sf of shared.folders) {
      map.set(sf.folderId, { id: sf.folderId, name: sf.name, parentId: sf.parentId });
    }
    for (const t of shared.templates) {
      if (t.folder) map.set(t.folder.id, { id: t.folder.id, name: t.folder.name, parentId: t.folder.parentId });
    }
    return Array.from(map.values());
  }, [shared.folders, shared.templates]);

  useEffect(() => {
    if (pickerMode != null) return;
    const state = location.state as ComposeListRestoreState | null;
    const restoreSelection = state?.restoreLibrarySelection;
    if (!restoreSelection) return;

    const fingerprint = JSON.stringify({
      sel: restoreSelection,
      tid: state.restoreLibraryTemplateId,
    });
    if (libraryRestoreAppliedRef.current === fingerprint) return;
    libraryRestoreAppliedRef.current = fingerprint;

    setSelection(restoreSelection);
    if (restoreSelection.root === 'shared') {
      setExpandedRoots((prev) => new Set(prev).add(ROOT_KEYS.shared));
      if (restoreSelection.sharedFolderId != null) {
        expandFolderBranch(
          restoreSelection.sharedFolderId,
          sharedChildFolders.map((f) => ({
            id: f.id,
            name: f.name,
            parentId: f.parentId,
            displayOrder: 0,
          })),
        );
      }
    } else if (restoreSelection.folderId != null) {
      const folders = restoreSelection.root === 'company' ? company.folders : personalFoldersMerged;
      expandFolderBranch(restoreSelection.folderId, folders);
    }
    if (state.restoreLibraryTemplateId != null) {
      setHighlightTemplateId(state.restoreLibraryTemplateId);
    }

    const {
      restoreLibrarySelection: _rs,
      restoreLibraryTemplateId: _rt,
      ...rest
    } = state;
    const nextState = Object.keys(rest).length > 0 ? rest : null;
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: nextState },
    );
  }, [
    location.pathname,
    location.search,
    location.hash,
    location.state,
    navigate,
    pickerMode,
    company.folders,
    personalFoldersMerged,
    sharedChildFolders,
    expandFolderBranch,
  ]);

  const sharedPermissionByFolderId = useMemo(() => {
    const map = new Map<number, { permission: 'read' | 'write'; ownerName: string }>();
    for (const sf of shared.folders) {
      for (const id of collectSharedSubtreeIds(sf.folderId, sf.includeSubfolders, sharedChildFolders)) {
        map.set(id, { permission: sf.permission, ownerName: sf.ownerName });
      }
    }
    return map;
  }, [shared.folders, sharedChildFolders]);

  const sharedWriteTree = useMemo(() => {
    const writableIds = new Set<number>();
    sharedPermissionByFolderId.forEach((meta, id) => {
      if (meta.permission === 'write') writableIds.add(id);
    });
    const flat: EmailTemplateFolder[] = [];
    for (const sf of shared.folders) {
      if (writableIds.has(sf.folderId)) {
        flat.push({
          id: sf.folderId,
          name: sf.name,
          parentId: sf.parentId,
          displayOrder: sf.displayOrder,
        });
      }
    }
    for (const f of sharedChildFolders) {
      if (writableIds.has(f.id) && !flat.some((x) => x.id === f.id)) {
        flat.push({
          id: f.id,
          name: f.name,
          parentId: f.parentId,
          displayOrder: 0,
        });
      }
    }
    return buildFolderTree(flat);
  }, [shared.folders, sharedChildFolders, sharedPermissionByFolderId]);

  const isRootSelected = (root: LibraryRoot) => {
    if (root === 'company') return selection.root === 'company' && selection.folderId === undefined;
    if (root === 'personal') return selection.root === 'personal' && selection.folderId === undefined;
    return selection.root === 'shared' && selection.sharedFolderId === undefined;
  };

  const hasFolderSelected =
    selection.root === 'shared' ? selection.sharedFolderId != null : selection.folderId != null;

  const visibleTemplates = useMemo(() => {
    if (showImport && !hasFolderSelected) return [];
    if (selection.root === 'company') {
      const pool = company.templates;
      if (selection.folderId === undefined) return pool;
      return pool.filter((t) => t.folderId === selection.folderId);
    }
    if (selection.root === 'personal') {
      const pool = personal.templates.filter((t) => t.folderId != null);
      if (selection.folderId === undefined) return pool;
      return pool.filter((t) => t.folderId === selection.folderId);
    }
    if (selection.root === 'shared' && selection.sharedFolderId != null) {
      return shared.templates.filter((t) => t.folderId === selection.sharedFolderId);
    }
    return shared.templates;
  }, [selection, company.templates, personal.templates, shared.templates, showImport, hasFolderSelected]);

  const visibleDocuments = useMemo(() => {
    if (selection.root === 'shared') return [];
    if (showImport && !hasFolderSelected) return [];
    if (selection.root === 'company') {
      return filterDocumentsByFolder(company.documents ?? [], selection.folderId);
    }
    return filterDocumentsByFolder(personal.documents ?? [], selection.folderId);
  }, [selection, company.documents, personal.documents, showImport, hasFolderSelected]);

  type LibraryListItem =
    | { kind: 'template'; template: EmailTemplate }
    | { kind: 'document'; document: EmailTemplateDocument };

  const isPicker = pickerMode != null;

  const visibleLibraryItems = useMemo((): LibraryListItem[] => {
    const items: LibraryListItem[] = [
      ...(pickerMode === 'document'
        ? []
        : visibleTemplates.map((template) => ({ kind: 'template' as const, template }))),
      ...(pickerMode === 'template'
        ? []
        : visibleDocuments.map((document) => ({ kind: 'document' as const, document }))),
    ];
    return items.sort((a, b) => {
      const nameA =
        a.kind === 'template'
          ? a.template.subject?.trim() || a.template.name || ''
          : a.document.name;
      const nameB =
        b.kind === 'template'
          ? b.template.subject?.trim() || b.template.name || ''
          : b.document.name;
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  }, [visibleTemplates, visibleDocuments, pickerMode]);

  useEffect(() => {
    if (highlightTemplateId == null) return;
    const scrollId = window.setTimeout(() => {
      const row = document.querySelector<HTMLElement>(`[data-library-template-id="${highlightTemplateId}"]`);
      row?.scrollIntoView({ block: 'nearest' });
    }, 0);
    const clearId = window.setTimeout(() => setHighlightTemplateId(null), 2500);
    return () => {
      window.clearTimeout(scrollId);
      window.clearTimeout(clearId);
    };
  }, [highlightTemplateId, visibleLibraryItems]);

  const openCreateFolder = (scope: 'PERSONAL' | 'COMPANY', parentId?: number, parentName?: string) => {
    setCreateFolderScope(scope);
    setCreateFolderParentId(parentId);
    setCreateFolderParentName(parentName);
    setCreateFolderOpen(true);
  };

  const openFolderOptions = (folder: FolderTreeNode, scope: 'PERSONAL' | 'COMPANY') => {
    if (scope === 'PERSONAL') {
      expandFolderBranch(folder.id, personalFoldersMerged);
    } else {
      expandFolderBranch(folder.id, company.folders);
    }
    setFolderOptions({ folder, scope });
  };

  const openSharedFolderOptions = (sf: SharedEmailTemplateFolder) => {
    setExpandedRoots((prev) => new Set(prev).add(ROOT_KEYS.shared));
    expandFolderBranch(sf.folderId, sharedChildFolders.map((f) => ({
      id: f.id,
      name: f.name,
      parentId: f.parentId,
      displayOrder: 0,
    })));
    const meta = sharedPermissionByFolderId.get(sf.folderId);
    const folder =
      findFolderNode(sharedWriteTree, sf.folderId) ??
      toFolderTreeNode(
        sf.folderId,
        sf.name,
        sharedChildFolders.find((f) => f.id === sf.folderId)?.parentId ?? sf.parentId,
      );
    setFolderOptions({
      folder,
      scope: 'SHARED',
      permission: meta?.permission ?? sf.permission,
      ownerName: meta?.ownerName ?? sf.ownerName,
    });
  };

  const handleFolderCreated = async () => {
    const folders = createFolderScope === 'COMPANY' ? company.folders : personalFoldersMerged;
    if (createFolderParentId != null) {
      expandFolderBranch(createFolderParentId, folders);
    }
    await onImported();
  };

  const apiErrorMessage = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

  const handleRenameFolder = async (folderId: number, name: string) => {
    try {
      await updateTemplateFolderApi(folderId, { name });
      await onImported();
      onSuccess('Folder renamed');
      setFolderOptions((prev) =>
        prev && prev.folder.id === folderId ? { ...prev, folder: { ...prev.folder, name } } : prev,
      );
    } catch (err: unknown) {
      onError(apiErrorMessage(err, 'Failed to rename folder'));
      throw err;
    }
  };

  const handleMoveFolder = async (folderId: number, parentId: number | null) => {
    try {
      await updateTemplateFolderApi(folderId, { parentId });
      await onImported();
      onSuccess('Folder moved');
    } catch (err: unknown) {
      onError(apiErrorMessage(err, 'Failed to move folder'));
      throw err;
    }
  };

  const handleCopyFolder = async (folderId: number, targetParentId: number) => {
    const resolved =
      folderOptions && findFolderNode(
        folderOptions.scope === 'COMPANY'
          ? companyTree
          : folderOptions.scope === 'PERSONAL'
            ? personalTree
            : sharedWriteTree,
        folderId,
      );
    const folderNode = resolved ?? folderOptions?.folder ?? null;
    const targetFolder =
      findFolderNode(companyTree, targetParentId) ?? findFolderNode(personalTree, targetParentId);
    const isShared = folderOptions?.scope === 'SHARED';
    const templatesPool = isShared
      ? shared.templates
      : folderOptions?.scope === 'COMPANY'
        ? company.templates
        : personal.templates;
    const documentsPool = isShared
      ? []
      : folderOptions?.scope === 'COMPANY'
        ? (company.documents ?? [])
        : (personal.documents ?? []);
    const templateCount = folderNode
      ? isShared
        ? countSharedFolderTemplates(folderNode.id, sharedChildFolders, shared.templates)
        : countTemplatesInFolder(folderNode, templatesPool)
      : 0;
    const documentCount = folderNode ? countDocumentsInFolder(folderNode, documentsPool) : 0;
    const subtreeSubfolderCount = folderNode ? countSubtreeSubfolders(folderNode) : 0;

    setCopyProgress({
      folderName: folderNode?.name ?? 'Folder',
      targetFolderName: targetFolder?.name ?? 'Destination',
      subfolderCount: subtreeSubfolderCount,
      templateCount,
      documentCount,
      phase: 'copying',
    });

    try {
      const result = await copyTemplateFolderApi(folderId, { targetParentId });
      setCopyProgress((prev) => (prev ? { ...prev, phase: 'done' } : null));
      await onImported();
      const parts = [
        `${result.foldersCopied} folder${result.foldersCopied === 1 ? '' : 's'}`,
        `${result.templatesCopied} template${result.templatesCopied === 1 ? '' : 's'}`,
        `${result.documentsCopied} document${result.documentsCopied === 1 ? '' : 's'}`,
      ];
      onSuccess(`Folder copied (${parts.join(', ')})`);
      window.setTimeout(() => setCopyProgress(null), 700);
    } catch (err: unknown) {
      setCopyProgress(null);
      onError(apiErrorMessage(err, 'Failed to copy folder'));
      throw err;
    }
  };

  const handleToggleFolderLock = async (folderId: number, locked: boolean) => {
    try {
      await updateTemplateFolderApi(folderId, { isLocked: locked });
      await onImported();
      onSuccess(locked ? 'Folder locked' : 'Folder unlocked');
    } catch (err: unknown) {
      onError(apiErrorMessage(err, locked ? 'Failed to lock folder' : 'Failed to unlock folder'));
      throw err;
    }
  };

  const handleDeleteFolder = async (folderId: number) => {
    const resolved =
      folderOptions && findFolderNode(
        folderOptions.scope === 'COMPANY'
          ? companyTree
          : folderOptions.scope === 'PERSONAL'
            ? personalTree
            : sharedWriteTree,
        folderId,
      );
    const folderNode = resolved ?? folderOptions?.folder ?? null;
    const isShared = folderOptions?.scope === 'SHARED';
    const templatesPool = isShared
      ? shared.templates
      : folderOptions?.scope === 'COMPANY'
        ? company.templates
        : personal.templates;
    const documentsPool = isShared
      ? []
      : folderOptions?.scope === 'COMPANY'
        ? (company.documents ?? [])
        : (personal.documents ?? []);
    const templateCount = folderNode
      ? isShared
        ? countSharedFolderTemplates(folderNode.id, sharedChildFolders, shared.templates)
        : countTemplatesInFolder(folderNode, templatesPool)
      : 0;
    const documentCount = folderNode ? countDocumentsInFolder(folderNode, documentsPool) : 0;
    const subtreeSubfolderCount = folderNode ? countSubtreeSubfolders(folderNode) : 0;

    setDeleteProgress({
      folderName: folderNode?.name ?? 'Folder',
      subfolderCount: subtreeSubfolderCount,
      templateCount,
      documentCount,
      phase: 'deleting',
    });

    try {
      const result = await deleteTemplateFolderApi(folderId);
      setDeleteProgress((prev) => (prev ? { ...prev, phase: 'done' } : null));
      await onImported();
      const parts = [
        `${result.foldersDeleted} folder${result.foldersDeleted === 1 ? '' : 's'}`,
        `${result.templatesDeleted} template${result.templatesDeleted === 1 ? '' : 's'}`,
        `${result.documentsDeleted} document${result.documentsDeleted === 1 ? '' : 's'}`,
      ];
      onSuccess(`Folder deleted (${parts.join(', ')})`);
      if (selection.root === 'company' && selection.folderId === folderId) {
        setSelection({ root: 'company' });
      }
      if (selection.root === 'personal' && selection.folderId === folderId) {
        setSelection({ root: 'personal' });
      }
      if (selection.root === 'shared' && selection.sharedFolderId === folderId) {
        setSelection({ root: 'shared' });
      }
      window.setTimeout(() => setDeleteProgress(null), 700);
    } catch (err: unknown) {
      setDeleteProgress(null);
      onError(apiErrorMessage(err, 'Failed to delete folder'));
      throw err;
    }
  };

  const handleDeleteTemplateConfirm = async () => {
    if (!templateToDelete) return;
    setDeletingTemplate(true);
    try {
      await deleteTemplateApi(templateToDelete.id);
      await onImported();
      onSuccess('Template deleted');
      setTemplateToDelete(null);
    } catch (err: unknown) {
      onError(apiErrorMessage(err, 'Failed to delete template'));
    } finally {
      setDeletingTemplate(false);
    }
  };

  const handleDeleteDocumentConfirm = async () => {
    if (!documentToDelete) return;
    setDeletingDocument(true);
    try {
      await deleteTemplateDocumentApi(documentToDelete.id);
      await onImported();
      onSuccess('Document deleted');
      setDocumentToDelete(null);
    } catch (err: unknown) {
      onError(apiErrorMessage(err, 'Failed to delete document'));
    } finally {
      setDeletingDocument(false);
    }
  };

  const handleDownloadDocument = async (doc: EmailTemplateDocument) => {
    try {
      await downloadTemplateDocumentApi(doc.id, doc.name);
    } catch (err: unknown) {
      onError(apiErrorMessage(err, 'Failed to download document'));
    }
  };

  const toggleRoot = (key: string) => {
    setExpandedRoots((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleFolder = (id: number) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isFolderExpanded = (id: number) => expandedFolderIds.has(id);
  const isCompanyFolderSelected = (id: number) => selection.root === 'company' && selection.folderId === id;
  const isPersonalFolderSelected = (id: number) =>
    selection.root === 'personal' && selection.folderId != null && selection.folderId === id;
  const isSharedFolderSelected = (folderId: number) =>
    selection.root === 'shared' && selection.sharedFolderId === folderId;

  const handleImportResult = async (result: {
    foldersCreated: number;
    templatesCreated: number;
    documentsCreated: number;
    skipped: number;
    errors: string[];
  }) => {
    await onImported();
    const parts = [
      result.templatesCreated > 0
        ? `${result.templatesCreated} template${result.templatesCreated === 1 ? '' : 's'} imported`
        : null,
      result.documentsCreated > 0
        ? `${result.documentsCreated} document${result.documentsCreated === 1 ? '' : 's'} imported`
        : null,
      result.foldersCreated > 0 ? `${result.foldersCreated} folder${result.foldersCreated === 1 ? '' : 's'} created` : null,
      result.skipped > 0 ? `${result.skipped} skipped` : null,
    ].filter(Boolean);
    onSuccess(parts.length > 0 ? parts.join(', ') : 'Import complete');
    if (result.errors.length > 0) onError(result.errors.slice(0, 3).join('; '));
  };

  const totalTemplates = company.templates.length + personal.templates.length + shared.templates.length;
  const totalDocuments = (company.documents?.length ?? 0) + (personal.documents?.length ?? 0);
  const hasLibraryItems = totalTemplates + totalDocuments > 0;
  const hasVisibleItems = visibleLibraryItems.length > 0;

  const selectionLabel = useMemo(() => {
    if (showImport && !hasFolderSelected) return 'Library';
    if (selection.root === 'company') {
      if (selection.folderId === undefined) return 'Company';
      return company.folders.find((f) => f.id === selection.folderId)?.name ?? 'Company';
    }
    if (selection.root === 'personal') {
      if (selection.folderId === undefined) return 'Personal';
      return personal.folders.find((f) => f.id === selection.folderId)?.name ?? 'Personal';
    }
    if (selection.sharedFolderId == null) return 'Shared';
    const sf = shared.folders.find((f) => f.folderId === selection.sharedFolderId);
    return sf ? `Shared · ${sf.name}` : 'Shared';
  }, [selection, company.folders, personal.folders, shared.folders, showImport, hasFolderSelected]);

  const uploadFolderId = useMemo((): number | null => {
    if (selection.root === 'shared') {
      if (selection.sharedFolderId == null) return null;
      const perm = sharedPermissionByFolderId.get(selection.sharedFolderId);
      if (perm?.permission !== 'write') return null;
      return selection.sharedFolderId;
    }
    if (selection.root === 'company') {
      if (!company.canEdit || selection.folderId === undefined) return null;
      const folder = company.folders.find((f) => f.id === selection.folderId);
      if (folder?.isSystemRoot) return null;
      if (isFolderPathLocked(selection.folderId, company.folders)) return null;
      return selection.folderId;
    }
    if (selection.folderId === undefined) return null;
    const folder = personal.folders.find((f) => f.id === selection.folderId);
    if (folder?.isSystemRoot) return null;
    if (isFolderPathLocked(selection.folderId, personal.folders)) return null;
    return selection.folderId;
  }, [selection, company.canEdit, company.folders, personal.folders, sharedPermissionByFolderId]);

  const handleNewFileClick = () => {
    if (uploadFolderId == null) {
      onError('Select an unlocked subfolder to upload into (not the library root).');
      return;
    }
    newFileInputRef.current?.click();
  };

  const toolbarIconBtn = cn(
    'inline-flex items-center justify-center flex-shrink-0 p-2 rounded-lg transition-colors disabled:opacity-50',
    colors.textMuted,
    colors.interactiveHover,
  );

  /** Target for folder import — Personal/Company roots are allowed (imported folder becomes a child). */
  const importTarget = useMemo((): { folderId: number; isLibraryRoot: boolean } | null => {
    if (selection.root === 'shared') {
      if (selection.sharedFolderId == null) return null;
      const perm = sharedPermissionByFolderId.get(selection.sharedFolderId);
      if (perm?.permission !== 'write') return null;
      return { folderId: selection.sharedFolderId, isLibraryRoot: false };
    }
    if (selection.root === 'company') {
      if (!company.canEdit) return null;
      if (selection.folderId === undefined) {
        const root = company.folders.find((f) => f.isSystemRoot);
        if (!root) return null;
        return { folderId: root.id, isLibraryRoot: true };
      }
      if (isFolderPathLocked(selection.folderId, company.folders)) return null;
      const folder = company.folders.find((f) => f.id === selection.folderId);
      return folder
        ? { folderId: folder.id, isLibraryRoot: folder.isSystemRoot === true }
        : null;
    }
    if (selection.folderId === undefined) {
      const root = personal.folders.find((f) => f.isSystemRoot);
      if (!root) return null;
      return { folderId: root.id, isLibraryRoot: true };
    }
    if (isFolderPathLocked(selection.folderId, personal.folders)) return null;
    const folder = personal.folders.find((f) => f.id === selection.folderId);
    return folder
      ? { folderId: folder.id, isLibraryRoot: folder.isSystemRoot === true }
      : null;
  }, [selection, company.canEdit, company.folders, personal.folders, sharedPermissionByFolderId]);

  const requireImportTargetFolder = (): { folderId: number; isLibraryRoot: boolean } | null => {
    if (importTarget == null) {
      onError('Select Personal, Company, or an unlocked folder to import into.');
      return null;
    }
    return importTarget;
  };

  const runFolderImport = async (
    files: File[],
    folderName: string,
    options?: { alreadyImporting?: boolean },
  ) => {
    if (!files.length) {
      onError('Folder contains no files to import');
      return;
    }

    const preflight = validateFolderImportFiles(files);
    if (!preflight.ok) {
      onError(preflight.message);
      if (options?.alreadyImporting) {
        setImporting(false);
        setImportProgress(null);
      }
      return;
    }
    if (preflight.warnLarge && !window.confirm(preflight.warnLarge)) {
      if (options?.alreadyImporting) {
        setImporting(false);
        setImportProgress(null);
      }
      return;
    }

    const target = requireImportTargetFolder();
    if (target == null) {
      if (options?.alreadyImporting) {
        setImporting(false);
        setImportProgress(null);
      }
      return;
    }

    const totalBytes = preflight.totalBytes;

    if (!options?.alreadyImporting) {
      setImporting(true);
    }
    setImportProgress({
      phase: 'uploading',
      folderName,
      scopeLabel: selectionLabel,
      fileCount: files.length,
      bytesTotal: totalBytes,
      bytesUploaded: 0,
      uploadPercent: 0,
    });

    try {
      const result = await importTemplatesFolder(files, target.folderId, {
        preserveTopLevelFolder: target.isLibraryRoot,
        onBatchProgress: ({
          phase,
          batchIndex,
          batchCount,
          filesTotal,
          filesCompleted,
          bytesTotal: batchBytesTotal,
          bytesUploaded,
          overallPercent,
          latestPath,
          currentFileIndex,
        }) => {
          setImportProgress((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              phase,
              batchIndex,
              batchCount,
              fileCount: filesTotal,
              filesCompleted,
              bytesUploaded,
              bytesTotal: batchBytesTotal > 0 ? batchBytesTotal : prev.bytesTotal ?? totalBytes,
              uploadPercent: overallPercent,
              overallPercent,
              latestPath: latestPath ?? (phase === 'uploading' ? prev.latestPath : undefined),
              currentFileIndex:
                currentFileIndex ?? (phase === 'uploading' ? prev.currentFileIndex : undefined),
            };
          });
        },
      });

      setImportProgress((prev) =>
        prev
          ? {
              ...prev,
              phase: 'completed',
              overallPercent: 100,
              filesCompleted: prev.fileCount,
              latestPath: undefined,
              currentFileIndex: undefined,
              completionSummary: {
                templatesCreated: result.templatesCreated,
                documentsCreated: result.documentsCreated,
                foldersCreated: result.foldersCreated,
                skipped: result.skipped,
              },
            }
          : prev,
      );
      await handleImportResult(result);
      setImporting(false);
    } catch (err: unknown) {
      onError(folderImportErrorMessage(err, 'Failed to import folder'));
      setImporting(false);
      setImportProgress(null);
    }
  };

  const handlePickImportSource = async () => {
    if (!isImportFolderAvailable()) {
      onError('Folder import is not available in this environment.');
      return;
    }
    if (requireImportTargetFolder() == null) return;

    setImportModalOpen(false);

    const pendingDropped = pendingLegacyFilesRef.current;
    if (pendingDropped?.length) {
      setFolderUploadConfirm({
        folderName: folderNameFromWebkitFiles(pendingDropped),
        mode: 'legacy',
      });
      return;
    }

    const picked = await pickImportFolder();
    if (picked === null) return;

    if (picked.mode === 'legacy') {
      folderInputRef.current?.click();
      return;
    }

    pendingDirectoryHandleRef.current = picked.handle;
    setFolderUploadConfirm({ folderName: picked.folderName, mode: 'directory' });
  };

  const handleFolderUploadConfirm = async () => {
    if (!folderUploadConfirm) return;
    const { folderName, mode } = folderUploadConfirm;
    setFolderUploadConfirm(null);

    if (mode === 'legacy') {
      const files = pendingLegacyFilesRef.current;
      pendingLegacyFilesRef.current = null;
      if (files?.length) await runFolderImport(files, folderName);
      return;
    }

    const handle = pendingDirectoryHandleRef.current;
    pendingDirectoryHandleRef.current = null;
    if (!handle) return;

    setImporting(true);
    setImportProgress({
      phase: 'reading',
      folderName,
      scopeLabel: selectionLabel,
      fileCount: 0,
      filesRead: 0,
    });

    try {
      const files = await readImportFolderFiles(handle, folderName, (info) => {
        setImportProgress((prev) =>
          prev
            ? {
                ...prev,
                filesRead: info.fileCount,
                latestPath: info.latestPath,
                fileCount: info.fileCount,
              }
            : prev,
        );
      });
      await runFolderImport(files, folderName, { alreadyImporting: true });
    } catch (err: unknown) {
      onError(folderImportErrorMessage(err, 'Failed to read folder'));
      setImporting(false);
      setImportProgress(null);
    }
  };

  const handleFolderUploadCancel = () => {
    setFolderUploadConfirm(null);
    pendingDirectoryHandleRef.current = null;
    pendingLegacyFilesRef.current = null;
  };

  const hasFileDrag = (e: React.DragEvent) => e.dataTransfer.types.includes('Files');

  const handleImportDragEnter = (e: React.DragEvent) => {
    if (!showImport || importing || !isImportFolderAvailable() || !hasFileDrag(e)) return;
    e.preventDefault();
    importDragDepthRef.current += 1;
    setImportDragOver(true);
  };

  const handleImportDragOver = (e: React.DragEvent) => {
    if (!showImport || importing || !isImportFolderAvailable() || !hasFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleImportDragLeave = () => {
    if (!showImport) return;
    importDragDepthRef.current = Math.max(0, importDragDepthRef.current - 1);
    if (importDragDepthRef.current === 0) setImportDragOver(false);
  };

  const handleImportDrop = async (e: React.DragEvent) => {
    if (!showImport || importing || !isImportFolderAvailable() || !hasFileDrag(e)) return;
    e.preventDefault();
    importDragDepthRef.current = 0;
    setImportDragOver(false);

    if (requireImportTargetFolder() == null) return;

    try {
      const { files, folderName } = await readDataTransferImportFiles(e.dataTransfer);
      if (!files.length) {
        onError('No files found in the dropped items');
        return;
      }
      pendingLegacyFilesRef.current = files;
      setFolderUploadConfirm({ folderName, mode: 'legacy' });
    } catch {
      onError('Could not read dropped files');
    }
  };

  const folderOptionsModal = folderOptions ? (() => {
    const isShared = folderOptions.scope === 'SHARED';
    const folderTree =
      folderOptions.scope === 'COMPANY'
        ? companyTree
        : folderOptions.scope === 'PERSONAL'
          ? personalTree
          : sharedWriteTree;
    const copyDestinationTrees = { company: companyTree, personal: personalTree };
    const resolvedFolder = findFolderNode(folderTree, folderOptions.folder.id) ?? folderOptions.folder;
    const templatesPool = isShared
      ? shared.templates
      : folderOptions.scope === 'COMPANY'
        ? company.templates
        : personal.templates;
    const canEdit = isShared
      ? folderOptions.permission === 'write'
      : resolvedFolder.isSystemRoot
        ? folderOptions.scope === 'PERSONAL' || company.canEdit
        : true;
    const documentsPool = isShared
      ? []
      : folderOptions.scope === 'COMPANY'
        ? (company.documents ?? [])
        : (personal.documents ?? []);
    const templateCount = isShared
      ? countSharedFolderTemplates(resolvedFolder.id, sharedChildFolders, shared.templates)
      : countTemplatesInFolder(resolvedFolder, templatesPool);
    const documentCount = countDocumentsInFolder(resolvedFolder, documentsPool);
    const subtreeSubfolderCount = countSubtreeSubfolders(resolvedFolder);
    const directTemplateCount = isShared
      ? countDirectTemplatesInFolder(resolvedFolder.id, shared.templates)
      : countDirectTemplatesInFolder(resolvedFolder.id, templatesPool);
    const subfolderCount = resolvedFolder.children.length;
    const folderPool =
      folderOptions.scope === 'COMPANY'
        ? company.folders
        : folderOptions.scope === 'PERSONAL'
          ? personal.folders
          : [];
    const pathLocked = isFolderPathLocked(resolvedFolder.id, folderPool);
    const ancestorLocked = hasLockedAncestor(resolvedFolder.id, folderPool);

    return (
      <TemplateFolderOptionsModal
        isOpen
        onClose={() => setFolderOptions(null)}
        folder={resolvedFolder}
        scope={folderOptions.scope}
        folderTree={folderTree}
        copyDestinationTrees={copyDestinationTrees}
        templateCount={templateCount}
        subtreeSubfolderCount={subtreeSubfolderCount}
        documentCount={documentCount}
        subfolderCount={subfolderCount}
        directTemplateCount={directTemplateCount}
        canEdit={canEdit}
        isPathLocked={pathLocked}
        hasLockedAncestor={ancestorLocked}
        isSystemRoot={resolvedFolder.isSystemRoot === true}
        ownerName={folderOptions.ownerName}
        onShare={folderOptions.scope === 'PERSONAL' ? onShareTemplateFolder : undefined}
        onRename={handleRenameFolder}
        onMove={handleMoveFolder}
        onCopy={handleCopyFolder}
        onToggleLock={
          !isShared && !resolvedFolder.isSystemRoot
            ? handleToggleFolderLock
            : undefined
        }
        onAddSubfolder={
          canEdit
            ? (folder) =>
                openCreateFolder(
                  isShared || folderOptions.scope === 'PERSONAL' ? 'PERSONAL' : 'COMPANY',
                  folder.id,
                  folder.name,
                )
            : undefined
        }
        onDelete={handleDeleteFolder}
      />
    );
  })() : null;

  return (
    <div
      className="relative flex-1 min-h-0 flex flex-col overflow-hidden"
      onDragEnter={showImport ? handleImportDragEnter : undefined}
      onDragOver={showImport ? handleImportDragOver : undefined}
      onDragLeave={showImport ? handleImportDragLeave : undefined}
      onDrop={showImport ? handleImportDrop : undefined}
    >
      {showImport && importDragOver && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
          aria-hidden
        >
          <div
            className={cn(
              'mx-4 max-w-md rounded-2xl border-2 border-dashed px-8 py-10 text-center shadow-lg',
              'border-violet-500 bg-white/95 dark:bg-gray-900/95',
            )}
          >
            <Upload className="mx-auto mb-3 h-10 w-10 text-violet-600 dark:text-violet-400" />
            <p className={cn('text-base font-semibold', colors.text)}>Drop folder to import</p>
            <p className={cn('text-sm mt-1', colors.textMuted)}>
              Into {selectionLabel} — subfolders inside the drop are preserved
            </p>
          </div>
        </div>
      )}
      <div className={cn('flex flex-1 min-h-0 flex-col overflow-hidden', showImport && importDragOver && 'opacity-60')}>
      <div className={cn('flex items-center justify-between gap-2 py-2 px-2 border-b flex-shrink-0', colors.border)}>
        <span className={cn('text-sm font-medium truncate', colors.textMuted)} title={selectionLabel}>
          {selectionLabel}
        </span>
        {!isPicker && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => navigateToCompose(navigate, '/emails/compose', undefined, composeReturnWithLibrary())}
            className={toolbarIconBtn}
            title="New message"
            aria-label="New message"
          >
            <MailPlus className="w-5 h-5" {...LIBRARY_TOOLBAR_ICON} aria-hidden />
          </button>
          <button
            type="button"
            disabled={uploadingFile}
            onClick={handleNewFileClick}
            className={cn(toolbarIconBtn, uploadingFile && 'cursor-wait')}
            title={
              uploadFolderId == null
                ? 'New file — select a subfolder first'
                : 'New file — upload PDF, Word, or Excel'
            }
            aria-label="New file"
          >
            {uploadingFile ? (
              <Loader2 className="w-5 h-5 animate-spin" {...LIBRARY_TOOLBAR_ICON} aria-hidden />
            ) : (
              <FilePlus className="w-5 h-5" {...LIBRARY_TOOLBAR_ICON} aria-hidden />
            )}
          </button>
          <input
            ref={newFileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file || uploadFolderId == null) return;
              setUploadingFile(true);
              try {
                await uploadTemplateDocumentApi(uploadFolderId, file);
                await onImported();
                onSuccess(`Uploaded "${file.name}"`);
              } catch (err: unknown) {
                onError(apiErrorMessage(err, 'Failed to upload file'));
              } finally {
                setUploadingFile(false);
              }
            }}
          />
        {showImport && (
          <div className="relative flex-shrink-0">
            <button
              type="button"
              disabled={importing}
              onClick={() => {
                if (requireImportTargetFolder() == null) return;
                setImportModalOpen(true);
              }}
              className={cn(
                toolbarIconBtn,
                importModalOpen && !importing && 'bg-gray-100 dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-600',
                importing && 'cursor-wait',
              )}
              title="Import"
              aria-label="Import"
            >
              {importing ? (
                <Loader2 className="w-5 h-5 animate-spin" {...LIBRARY_TOOLBAR_ICON} aria-hidden />
              ) : (
                <Upload className="w-5 h-5" {...LIBRARY_TOOLBAR_ICON} aria-hidden />
              )}
            </button>
            <input
              ref={folderInputRef}
              type="file"
              className="hidden"
              multiple
              // @ts-expect-error webkitdirectory
              webkitdirectory=""
              onChange={(e) => {
                const fileList = e.target.files;
                e.target.value = '';
                if (!fileList?.length) return;
                const files = Array.from(fileList);
                pendingLegacyFilesRef.current = files;
                setFolderUploadConfirm({
                  folderName: folderNameFromWebkitFiles(files),
                  mode: 'legacy',
                });
              }}
            />
            <ImportTemplatesLibraryModal
              isOpen={importModalOpen}
              onClose={() => {
                setImportModalOpen(false);
                if (!folderUploadConfirm) {
                  pendingLegacyFilesRef.current = null;
                }
              }}
              targetFolderName={selectionLabel}
              folderImportAvailable={isImportFolderAvailable()}
              lanDevImport={isImportFolderLanDev()}
              onPickImportSource={() => void handlePickImportSource()}
              importing={importing}
            />
            <TemplateFolderImportProgressModal
              progress={importProgress}
              onDismiss={() => setImportProgress(null)}
            />
            <TemplateFolderDeleteProgressModal progress={deleteProgress} />
            <TemplateFolderCopyProgressModal progress={copyProgress} />
            <ConfirmationDialog
              isOpen={folderUploadConfirm != null}
              onClose={handleFolderUploadCancel}
              onConfirm={() => void handleFolderUploadConfirm()}
              title="Confirm folder import"
              message={
                folderUploadConfirm
                  ? `Import files from "${folderUploadConfirm.folderName}" into "${selectionLabel}"? Only continue if you trust these files.`
                  : ''
              }
              confirmText="Upload"
              cancelText="Cancel"
              confirmVariant="default"
              isLoading={importing}
              zIndex={60}
            />
          </div>
        )}
        </div>
        )}
      </div>

      <div className="library-split-container relative flex-1 min-h-0 flex h-full overflow-hidden">
        <div
          className={cn(
            'min-w-0 h-full min-h-0 border-r overflow-y-auto overflow-x-hidden py-1 pl-1 pr-0.5 scrollbar-thin',
            colors.border,
          )}
          style={{ width: `${folderSplitPercent}%` }}
          role="tree"
          aria-label="Library"
        >
          {companyTree.map((node) => (
            <div
              key={node.id}
              className={cn('mt-1.5 pt-1.5 border-t first:mt-0 first:pt-0 first:border-t-0', colors.border)}
            >
              <TemplateFolderTree
                node={node}
                depth={0}
                isSelected={isCompanyFolderSelected}
                isExpanded={isFolderExpanded}
                onToggleExpand={toggleFolder}
                onSelectFolder={(id) => setSelection({ root: 'company', folderId: id })}
                {...(!isPicker && company.canEdit
                  ? { onOpenFolderOptions: (folder) => openFolderOptions(folder, 'COMPANY') }
                  : {})}
                isPathLocked={(id) => isFolderPathLocked(id, company.folders)}
                {...rowProps}
                rowLevel1={rowLevel1}
              />
            </div>
          ))}

          {personalTree.map((node) => (
            <div
              key={node.id}
              className={cn('mt-1.5 pt-1.5 border-t', colors.border)}
            >
              <TemplateFolderTree
                node={node}
                depth={0}
                isSelected={isPersonalFolderSelected}
                isExpanded={isFolderExpanded}
                onToggleExpand={toggleFolder}
                onSelectFolder={(id) => {
                  expandFolderBranch(id, personalFoldersMerged);
                  setSelection({ root: 'personal', folderId: id });
                }}
                {...(!isPicker
                  ? { onOpenFolderOptions: (folder) => openFolderOptions(folder, 'PERSONAL') }
                  : {})}
                isPathLocked={(id) => isFolderPathLocked(id, personal.folders)}
                {...rowProps}
                rowLevel1={rowLevel1}
              />
            </div>
          ))}

          <TemplateLibraryRootRow
            label="Shared"
            expanded={expandedRoots.has(ROOT_KEYS.shared)}
            onToggleExpand={() => toggleRoot(ROOT_KEYS.shared)}
            selected={isRootSelected('shared')}
            onSelect={() => setSelection({ root: 'shared' })}
            {...rowProps}
          >
            {shared.folders.length === 0 ? (
              <p className={cn('py-2 pl-2 text-xs', colors.textMuted)}>No folders shared with you.</p>
            ) : (
              shared.folders
                .filter((sf) => sf.parentId == null || !shared.folders.some((s) => s.folderId === sf.parentId))
                .map((sf) => (
                  <SharedTemplateFolderTree
                    key={sf.shareId}
                    sf={sf}
                    depth={1}
                    childFolders={sharedChildFolders}
                    isSelected={isSharedFolderSelected}
                    isExpanded={isFolderExpanded}
                    onToggleExpand={toggleFolder}
                    onSelect={(folderId) => setSelection({ root: 'shared', sharedFolderId: folderId })}
                    {...(!isPicker ? { onOpenFolderOptions: openSharedFolderOptions } : {})}
                    {...rowProps}
                    rowLevel1={rowLevel1}
                  />
                ))
            )}
          </TemplateLibraryRootRow>
        </div>

        <div
          className={cn(
            'absolute top-0 bottom-0 w-1 cursor-col-resize transition-colors z-10 touch-none',
            colors.surfaceHover,
            'hover:bg-indigo-500',
            isResizingFolderSplit && colors.primary,
          )}
          // Keep hit target on the right pane so the folder list scrollbar stays usable.
          style={{ left: `${folderSplitPercent}%` }}
          onMouseDown={handleFolderSplitMouseDown}
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={Math.round(folderSplitPercent)}
          aria-valuemin={LIBRARY_FOLDER_SPLIT_MIN}
          aria-valuemax={LIBRARY_FOLDER_SPLIT_MAX}
          aria-label="Resize folder panel"
        >
          <div className="absolute inset-y-0 left-0 w-4" aria-hidden />
        </div>

        <div
          className="min-w-0 h-full min-h-0 overflow-y-auto overflow-x-hidden p-2 scrollbar-thin"
          style={{ width: `${100 - folderSplitPercent}%` }}
        >
          {isLoading ? (
            <p className={cn('py-6 px-4 text-sm', colors.textMuted)}>Loading library…</p>
          ) : !hasVisibleItems ? (
            <div className={cn('py-6 px-4 text-sm space-y-4', colors.textMuted)}>
              <p>
                {showImport && hasLibraryItems && !hasFolderSelected
                  ? 'Select a folder on the left to view templates and documents.'
                  : hasLibraryItems
                  ? pickerMode === 'document'
                    ? 'No documents in this folder. Select another folder on the left.'
                    : pickerMode === 'template'
                      ? 'No templates in this folder. Select another folder on the left.'
                      : 'No templates or documents in this folder.'
                  : pickerMode === 'document'
                    ? 'No library documents yet. Upload them from the Library page.'
                    : isPicker
                      ? 'Library is empty.'
                      : showImport
                        ? 'Library is empty. Select a subfolder on the left, then import or drag files into it.'
                        : 'Library is empty. Create folders, use New Message or New File, or save from Compose.'}
              </p>
              {!hasLibraryItems && !isPicker && (
                <p className="text-xs">
                  Use <strong className="font-medium">New Message</strong> to compose a template, or{' '}
                  <strong className="font-medium">New File</strong> to upload a reference document (select a subfolder first).
                </p>
              )}
            </div>
          ) : (
            <ul className="space-y-1">
              {visibleLibraryItems.map((item) => {
                const itemPathLocked =
                  item.kind === 'template'
                    ? isLibraryItemPathLocked(
                        item.template.folderId,
                        item.template.scope,
                        company.folders,
                        personal.folders,
                      )
                    : isLibraryItemPathLocked(
                        item.document.folderId,
                        item.document.scope,
                        company.folders,
                        personal.folders,
                      );

                return item.kind === 'template' ? (
                  <li key={`t-${item.template.id}`} className="group flex items-stretch gap-1">
                    <button
                      type="button"
                      data-library-template-id={item.template.id}
                      onClick={() => {
                        if (pickerMode === 'template' && onPickTemplate) {
                          onPickTemplate(item.template);
                          return;
                        }
                        navigateToCompose(
                          navigate,
                          '/emails/compose',
                          { template: item.template },
                          composeReturnWithLibrary(item.template.id),
                        );
                      }}
                      className={cn(
                        'flex-1 min-w-0 flex items-start gap-3 text-left rounded-lg border px-3 py-2.5 transition-colors',
                        colors.border,
                        colors.interactiveHover,
                        colors.text,
                        highlightTemplateId === item.template.id && 'ring-2 ring-indigo-500',
                      )}
                    >
                      <FileIcon fileName={libraryItemIconFileName(item)} size="xs" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate flex items-center gap-1.5">
                          <span className="truncate">
                            {item.template.subject?.trim() || item.template.name || '(No name)'}
                          </span>
                          {itemPathLocked ? (
                            <span title="Locked" className="inline-flex flex-shrink-0">
                              <Lock className="w-3.5 h-3.5 opacity-70" aria-hidden />
                            </span>
                          ) : null}
                        </div>
                        {item.template.ownerName && (
                          <div className={cn('text-[10px] mt-1 truncate', colors.textMuted)}>
                            Shared by {item.template.ownerName}
                          </div>
                        )}
                        {item.template.folder?.name && (
                          <div className={cn('text-[10px] mt-1 truncate', colors.textMuted)}>
                            {item.template.folder.name}
                          </div>
                        )}
                        {item.template.updatedAt && (
                          <div className={cn('text-[10px] mt-1', colors.textMuted)}>
                            {formatDate(item.template.updatedAt)}
                          </div>
                        )}
                      </div>
                    </button>
                    {!isPicker && !itemPathLocked && (
                      <button
                        type="button"
                        onClick={() =>
                          setCustomFieldsTarget({
                            kind: 'template',
                            id: item.template.id,
                            label:
                              item.template.subject?.trim() ||
                              item.template.name ||
                              `Template #${item.template.id}`,
                          })
                        }
                        className={cn(
                          'flex-shrink-0 self-center rounded-lg border p-2 transition-colors opacity-70 group-hover:opacity-100',
                          colors.border,
                          colors.interactiveHover,
                        )}
                        title="Custom fields"
                        aria-label={`Custom fields for ${item.template.name || item.template.subject || item.template.id}`}
                      >
                        <SlidersHorizontal className="w-4 h-4" />
                      </button>
                    )}
                    {!isPicker && canDeleteTemplate(item.template, company.canEdit, company.folders, personal.folders) && (
                      <button
                        type="button"
                        onClick={() => setTemplateToDelete(item.template)}
                        className={cn(
                          'flex-shrink-0 self-center rounded-lg border p-2 transition-colors opacity-70 group-hover:opacity-100',
                          colors.border,
                          'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20',
                        )}
                        title="Delete template"
                        aria-label={`Delete template ${item.template.name || item.template.subject || item.template.id}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </li>
                ) : (
                  <li key={`d-${item.document.id}`} className="group flex items-stretch gap-1">
                    {pickerMode === 'document' ? (
                      <label
                        className={cn(
                          'flex-1 min-w-0 flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors',
                          colors.border,
                          colors.interactiveHover,
                          colors.text,
                          selectedDocumentIds?.has(item.document.id) && 'ring-2 ring-indigo-500/40 bg-indigo-50/50 dark:bg-indigo-950/20',
                        )}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 flex-shrink-0"
                          checked={selectedDocumentIds?.has(item.document.id) ?? false}
                          onChange={() => onToggleDocument?.(item.document.id)}
                        />
                        <FileIcon fileName={libraryItemIconFileName(item)} size="xs" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate flex items-center gap-1.5">
                            <span className="truncate">{item.document.name}</span>
                            {itemPathLocked ? (
                              <span title="Locked" className="inline-flex flex-shrink-0">
                                <Lock className="w-3.5 h-3.5 opacity-70" aria-hidden />
                              </span>
                            ) : null}
                          </div>
                          <div className={cn('text-xs truncate mt-0.5', colors.textMuted)}>
                            {formatFileSize(item.document.sizeBytes)}
                            {item.document.createdAt ? ` · ${formatDate(item.document.createdAt)}` : ''}
                          </div>
                        </div>
                      </label>
                    ) : (
                      <>
                    <button
                      type="button"
                      onClick={() => handleDownloadDocument(item.document)}
                      className={cn(
                        'flex-1 min-w-0 flex items-start gap-3 text-left rounded-lg border px-3 py-2.5 transition-colors',
                        colors.border,
                        colors.interactiveHover,
                        colors.text,
                      )}
                    >
                      <FileIcon fileName={libraryItemIconFileName(item)} size="xs" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate flex items-center gap-1.5">
                          <span className="truncate">{item.document.name}</span>
                          {itemPathLocked ? (
                            <span title="Locked" className="inline-flex flex-shrink-0">
                              <Lock className="w-3.5 h-3.5 opacity-70" aria-hidden />
                            </span>
                          ) : null}
                        </div>
                        <div className={cn('text-xs truncate mt-0.5', colors.textMuted)}>
                          {formatFileSize(item.document.sizeBytes)}
                          {item.document.createdAt ? ` · ${formatDate(item.document.createdAt)}` : ''}
                        </div>
                      </div>
                    </button>
                    {!isPicker && !itemPathLocked && (
                      <button
                        type="button"
                        onClick={() =>
                          setCustomFieldsTarget({
                            kind: 'document',
                            id: item.document.id,
                            label: item.document.name,
                          })
                        }
                        className={cn(
                          'flex-shrink-0 self-center rounded-lg border p-2 transition-colors opacity-70 group-hover:opacity-100',
                          colors.border,
                          colors.interactiveHover,
                        )}
                        title="Custom fields"
                        aria-label={`Custom fields for ${item.document.name}`}
                      >
                        <SlidersHorizontal className="w-4 h-4" />
                      </button>
                    )}
                    {canDeleteDocument(item.document, company.canEdit, company.folders, personal.folders) && (
                      <button
                        type="button"
                        onClick={() => setDocumentToDelete(item.document)}
                        className={cn(
                          'flex-shrink-0 self-center rounded-lg border p-2 transition-colors opacity-70 group-hover:opacity-100',
                          colors.border,
                          'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20',
                        )}
                        title="Delete document"
                        aria-label={`Delete document ${item.document.name}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      </div>

      {folderOptionsModal}

      {createFolderOpen && (
        <CreateTemplateFolderModal
          isOpen={createFolderOpen}
          onClose={() => setCreateFolderOpen(false)}
          scope={createFolderScope}
          parentId={createFolderParentId}
          parentName={createFolderParentName}
          onCreated={handleFolderCreated}
        />
      )}

      <Modal isOpen={customFieldsTarget != null} onClose={() => setCustomFieldsTarget(null)} size="md">
        <ModalHeader>
          <ModalTitle>
            Custom fields
            {customFieldsTarget ? ` — ${customFieldsTarget.label}` : ''}
          </ModalTitle>
        </ModalHeader>
        <ModalContent>
          {customFieldsTarget ? (
            <EntityCustomFieldsSection
              entityType={
                customFieldsTarget.kind === 'template'
                  ? CUSTOM_FIELD_ENTITY.EMAIL_TEMPLATE
                  : CUSTOM_FIELD_ENTITY.EMAIL_TEMPLATE_DOCUMENT
              }
              entityId={customFieldsTarget.id}
              title=""
              emptyMessage="No custom fields are defined for this item yet. Add them under Custom Fields in settings."
            />
          ) : null}
        </ModalContent>
      </Modal>

      <ConfirmationDialog
        isOpen={templateToDelete != null}
        onClose={() => !deletingTemplate && setTemplateToDelete(null)}
        onConfirm={handleDeleteTemplateConfirm}
        title="Delete template"
        message={
          templateToDelete
            ? `Delete "${templateToDelete.name || templateToDelete.subject || 'this template'}"? This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={deletingTemplate}
      />

      <ConfirmationDialog
        isOpen={documentToDelete != null}
        onClose={() => !deletingDocument && setDocumentToDelete(null)}
        onConfirm={handleDeleteDocumentConfirm}
        title="Delete document"
        message={
          documentToDelete
            ? `Delete "${documentToDelete.name}"? This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={deletingDocument}
      />
    </div>
  );
};

export default TemplatesLibraryPanel;
